import React, { useState, useRef, useEffect, useCallback } from 'react';
import './form.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTicket,
  faUser,
  faEnvelope,
  faShieldHalved,
  faLock,
  faArrowRight,
  faCheck,
  faCircleInfo,
  faSpinner,
  faXmark,
  faCircleCheck,
  faCircleXmark,
  faInfoCircle,
  faChevronDown,
  faMagnifyingGlass,
  faBuilding,
} from '@fortawesome/free-solid-svg-icons';

/* ─────────── HALL LIST ─────────── */
const HALLS = [
  { value: "ABP Hall", label: "ABP" },
  { value: "Azad", label: "Azad" },
  { value: "BC Roy", label: "BC Roy" },
  { value: "BRH", label: "BRH" },
  { value: "Gokhale", label: "Gokhale" },
  { value: "HJB", label: "HJB" },
  { value: "JCB", label: "JCB" },
  { value: "LBS", label: "LBS" },
  { value: "LLR", label: "LLR" },
  { value: "MMM", label: "MMM" },
  { value: "MS", label: "MS" },
  { value: "MT", label: "MT" },
  { value: "Nehru", label: "Nehru" },
  { value: "Patel", label: "Patel" },
  { value: "RK", label: "RK" },
  { value: "RP", label: "RP" },
  { value: "RLB", label: "RLB" },
  { value: "SAM", label: "SAM" },
  { value: "SBP", label: "SBP" },
  { value: "SNIG", label: "SNIG" },
  { value: "SNVH", label: "SNVH" },
  { value: "VS", label: "VS" },
  { value: "VSRC1", label: "VSRC 1" },
  { value: "VSRC2", label: "VSRC 2" },
  { value: "Zakir Hussain Hall", label: "Zakir Hussain Hall" },
];
const PRICE = 350;

/* ══════════════════════════════════════
   API BASE URL
   ══════════════════════════════════════ */
// Strip any trailing slash so fetch URLs never get a double-slash
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

/* ── Load Razorpay script ── */
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

/* ── API Functions ── */
async function sendOtpAPI(email) {
  const res = await fetch(`${API_BASE}/api/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return res.json();
}

async function verifyOtpAPI(email, otp) {
  const res = await fetch(`${API_BASE}/api/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, otp }),
  });
  return res.json();
}

async function createOrderAPI(data) {
  const res = await fetch(`${API_BASE}/api/payment/create-order`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return res.json();
}

/* ─── Toast Component ─── */
function Toast({ toast, onDone }) {
  const [exit, setExit] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setExit(true), 3200);
    const t2 = setTimeout(onDone, 3500);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [onDone]);

  const iconMap = { success: faCircleCheck, error: faCircleXmark, info: faInfoCircle };
  return (
    <div className={`jm-toast ${toast.type} ${exit ? 'jm-toast-exit' : ''}`}>
      <FontAwesomeIcon icon={iconMap[toast.type] || faInfoCircle} />
      {toast.msg}
    </div>
  );
}

/* ─── Custom Dropdown Component ─── */
function HallDropdown({ value, onChange, error }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 50);
  }, [open]);

  // Filter halls by label (display text)
  const filtered = HALLS.filter(h => 
    h.label.toLowerCase().includes(search.toLowerCase())
  );

  // Get display label for current value
  const displayLabel = value 
    ? HALLS.find(h => h.value === value)?.label || value 
    : '';

  const select = (hall) => {
    onChange(hall.value);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className={`jm-dropdown ${open ? 'open' : ''}`} ref={ref}>
      <button
        type="button"
        className={`jm-dropdown-trigger ${!value ? 'placeholder' : ''} ${open ? 'open' : ''} ${error ? 'err' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        {displayLabel || 'Select your hall'}
        <span className="jm-dropdown-arrow">
          <FontAwesomeIcon icon={faChevronDown} />
        </span>
      </button>
      {open && (
        <div className="jm-dropdown-menu">
          <div className="jm-dropdown-search">
            <input ref={searchRef} type="text" placeholder="Search halls..."
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {filtered.length > 0 ? (
            filtered.map(h => (
              <div key={h.value}
                className={`jm-dropdown-option ${value === h.value ? 'selected' : ''}`}
                onClick={() => select(h)}>
                {value === h.value && <FontAwesomeIcon icon={faCheck} className="opt-check" />}
                {h.label}
              </div>
            ))
          ) : (
            <div className="jm-dropdown-empty">
              <FontAwesomeIcon icon={faMagnifyingGlass} /> No halls found
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════
   REGISTER FORM COMPONENT
   ═══════════════════════════════════ */
export default function RegisterForm() {
  /* ── Init AOS ── */
  useEffect(() => { AOS.init({ duration: 600, once: true, offset: 60 }); }, []);

  /* ── Form state ── */
  const [form, setForm] = useState({
    name: '', phone: '', roll: '', hall: '', email: '', passes: '1'
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  /* ── OTP state ── */
  const [otpState, setOtpState] = useState('idle'); // idle | sending | sent | verifying | verified | failed
  const [otp, setOtp] = useState(['', '', '', '']);
  const [timer, setTimer] = useState(0);
  const otpRefs = [useRef(), useRef(), useRef(), useRef()];
  const timerRef = useRef(null);

  /* ── Timer logic ── */
  useEffect(() => {
    if (timer <= 0) { clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(() => setTimer(t => t - 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [timer]);

  /* ── Helpers ── */
  const showToast = (msg, type = 'info') => setToast({ msg, type });
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }));
    if (name === 'email' && otpState !== 'idle') { setOtpState('idle'); setOtp(['', '', '', '']); }
  };

  /* Hall change (custom dropdown) */
  const handleHallChange = (hall) => {
    setForm(f => ({ ...f, hall }));
    if (errors.hall) setErrors(er => ({ ...er, hall: '' }));
  };

  /* ── Validation ── */
  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!/^\d{10}$/.test(form.phone)) e.phone = 'Enter a valid 10-digit number';
    if (!form.roll.trim()) e.roll = 'Roll number is required';
    if (!form.hall) e.hall = 'Select your hall';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    else if (otpState !== 'verified') e.email = 'Please verify your email';
    if (!form.passes || parseInt(form.passes, 10) < 1) e.passes = 'At least 1 pass';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── Send OTP ── */
  const sendOtp = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setErrors(er => ({ ...er, email: 'Enter a valid email first' }));
      return;
    }
    setOtpState('sending');
    try {
      const res = await sendOtpAPI(form.email);
      if (!res.success) throw new Error(res.message || 'Failed');
      setOtpState('sent');
      setOtp(['', '', '', '']);
      setTimer(120);
      showToast('OTP sent to your email!', 'success');
      setTimeout(() => otpRefs[0].current?.focus(), 200);
    } catch (err) {
      setOtpState('idle');
      showToast(err.message || 'Failed to send OTP. Try again.', 'error');
    }
  };

  /* ── OTP input handler ── */
  const handleOtpChange = useCallback((idx, val) => {
    if (!/^\d?$/.test(val)) return;
    setOtp(prev => {
      const next = [...prev];
      next[idx] = val;
      // Auto-focus next
      if (val && idx < 3) setTimeout(() => otpRefs[idx + 1].current?.focus(), 30);
      // Auto-verify when all 4 filled
      if (val && next.every(d => d !== '')) {
        setTimeout(() => verifyOtp(next.join('')), 150);
      }
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.email]);

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      otpRefs[idx - 1].current?.focus();
    }
  };
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const data = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (!data) return;
    const next = ['', '', '', ''];
    data.split('').forEach((ch, i) => { next[i] = ch; });
    setOtp(next);
    if (next.every(d => d)) setTimeout(() => verifyOtp(next.join('')), 150);
  };

  /* ── Verify OTP ── */
  const verifyOtp = async (code) => {
    setOtpState('verifying');
    try {
      const data = await verifyOtpAPI(form.email, code);
      if (data.success) {
        setOtpState('verified');
        setErrors(er => ({ ...er, email: '' }));
        showToast('Email verified successfully!', 'success');
      } else {
        setOtpState('failed');
        showToast(data.message || 'Invalid OTP. Please try again.', 'error');
      }
    } catch {
      setOtpState('failed');
      showToast('Verification failed. Try again.', 'error');
    }
  };

  /* ── Submit & Open Razorpay Checkout ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      showToast('Please fix the errors above.', 'error');
      return;
    }
    setLoading(true);
    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        showToast('Failed to load payment gateway.', 'error');
        setLoading(false);
        return;
      }

      // Create order on backend
      const orderData = await createOrderAPI({
        name: form.name, 
        phone: form.phone, 
        roll: form.roll,
        hall: form.hall, 
        email: form.email, 
        passes: parseInt(form.passes, 10)
      });

      if (!orderData.success) {
        throw new Error(orderData.message || 'Failed to create order');
      }

      // Open Razorpay checkout
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: 'INR',
        name: 'RCA - Khamma Ghani',
        description: `${form.passes} Pass(es) for The Aurelia Mirage`,
        order_id: orderData.order_id,
        prefill: {
          name: form.name,
          email: form.email,
          contact: form.phone
        },
        theme: {
          color: '#D4A853'
        },
        handler: async function (response) {
          // Webhook handles DB update + email server-side.
          // Poll GET /api/payment/order/:id until webhook marks it paid, then
          // redirect to the success page with the QR code.
          setLoading(true);
          const orderId = response.razorpay_order_id;
          try {
            let result = null;
            for (let i = 0; i < 15; i++) {        // up to ~15 seconds
              await new Promise(r => setTimeout(r, 1000));
              const res = await fetch(`${API_BASE}/api/payment/order/${encodeURIComponent(orderId)}`);
              const data = await res.json();
              if (data.success && data.order?.status === 'paid' && data.qr_code) {
                result = data;
                break;
              }
            }
            if (result) {
              const params = new URLSearchParams({
                name: form.name,
                email: form.email,
                passes: form.passes,
                qr: result.qr_code,
                qrUrl: result.qr_url || '',
              });
              window.location.href = `/Payment?${params.toString()}`;
            } else {
              // Webhook hasn't processed yet — send to email-confirmation page
              window.location.href = '/registrationsuccessful';
            }
          } catch (err) {
            console.error('Post-payment redirect error:', err);
            window.location.href = '/registrationsuccessful';
          }
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
            showToast('Payment cancelled.', 'info');
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        setLoading(false);
        showToast(`Payment failed: ${response.error.description}`, 'error');
      });
      rzp.open();

    } catch (err) {
      showToast(err.message || 'Network error. Please retry.', 'error');
      setLoading(false);
    }
  };

  /* ── Progress step helper ── */
  const step = otpState === 'verified' ? 3 : ['sent', 'verifying', 'failed'].includes(otpState) ? 2 : 1;

  return (
    <div className="jm-reg-wrapper">
      {toast && <Toast toast={toast} onDone={() => setToast(null)} />}

      <div className="jm-card" data-aos="fade-up">
        {/* ── Header ── */}
        <div className="jm-card-header">
          <div className="jm-header-icon">
            <FontAwesomeIcon icon={faTicket} />
          </div>
          <h1 id="form-heading">Event Registration</h1>
          <p>Jaisalmer — The Aurelia Mirage</p>
        </div>

        {/* ── Progress Bar ── */}
        <div className="jm-progress-bar">
          <div className={`jm-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'done' : ''}`}>
            <span className="jm-step-num">{step > 1 ? <FontAwesomeIcon icon={faCheck} /> : '1'}</span>
            <span className="jm-step-text">Details</span>
          </div>
          <div className={`jm-step-line ${step >= 2 ? 'active' : ''}`} />
          <div className={`jm-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'done' : ''}`}>
            <span className="jm-step-num">{step > 2 ? <FontAwesomeIcon icon={faCheck} /> : '2'}</span>
            <span className="jm-step-text">Verify Email</span>
          </div>
          <div className={`jm-step-line ${step >= 3 ? 'active' : ''}`} />
          <div className={`jm-step ${step >= 3 ? 'active' : ''}`}>
            <span className="jm-step-num">3</span>
            <span className="jm-step-text">Payment</span>
          </div>
        </div>

        {/* ── Form Body ── */}
        <form className="jm-form-body" onSubmit={handleSubmit} noValidate>

          {/* Info notice */}
          <div className="jm-notice" data-aos="fade-right" data-aos-delay="100">
            <FontAwesomeIcon icon={faCircleInfo} className="notice-icon" />
            <p>Fill in your details below to register for the event. Each pass costs <strong>₹{PRICE}</strong>. Email verification is required before proceeding to payment.</p>
          </div>

          {/* ── Section: Personal ── */}
          <div className="jm-divider" data-aos="fade-up" data-aos-delay="150">
            <span><FontAwesomeIcon icon={faUser} /> &nbsp;Personal Information</span>
          </div>

          <div className="jm-grid">
            {/* Name */}
            <div className="jm-field" data-aos="fade-right" data-aos-delay="200">
              <label htmlFor="jm-name">Full Name</label>
              <input id="jm-name" name="name" className={`jm-input ${errors.name ? 'err' : ''}`}
                placeholder="Enter your full name" value={form.name} onChange={handleChange} />
              {errors.name && <div className="jm-err-msg"><FontAwesomeIcon icon={faXmark} /> {errors.name}</div>}
            </div>

            {/* Phone */}
            <div className="jm-field" data-aos="fade-left" data-aos-delay="200">
              <label htmlFor="jm-phone">Phone Number</label>
              <input id="jm-phone" name="phone" type="tel" className={`jm-input ${errors.phone ? 'err' : ''}`}
                placeholder="10-digit mobile number" value={form.phone} onChange={handleChange} maxLength={10} />
              {errors.phone && <div className="jm-err-msg"><FontAwesomeIcon icon={faXmark} /> {errors.phone}</div>}
            </div>

            {/* Roll */}
            <div className="jm-field" data-aos="fade-right" data-aos-delay="250">
              <label htmlFor="jm-roll">Roll Number</label>
              <input id="jm-roll" name="roll" className={`jm-input ${errors.roll ? 'err' : ''}`}
                placeholder="e.g. 2023UGCS001" value={form.roll} onChange={handleChange} />
              {errors.roll && <div className="jm-err-msg"><FontAwesomeIcon icon={faXmark} /> {errors.roll}</div>}
            </div>

            {/* Hall — Custom Dropdown */}
            <div className="jm-field" data-aos="fade-left" data-aos-delay="250">
              <label><FontAwesomeIcon icon={faBuilding} style={{ marginRight: 4 }} /> Hall of Residence</label>
              <HallDropdown value={form.hall} onChange={handleHallChange} error={errors.hall} />
              {errors.hall && <div className="jm-err-msg"><FontAwesomeIcon icon={faXmark} /> {errors.hall}</div>}
            </div>
          </div>

          {/* ── Section: Email ── */}
          <div className="jm-divider" data-aos="fade-up" data-aos-delay="100">
            <span><FontAwesomeIcon icon={faEnvelope} /> &nbsp;Email Verification</span>
          </div>

          <div className="jm-field full-w" data-aos="fade-up" data-aos-delay="150">
            <label htmlFor="jm-email">Email Address</label>
            <div className="jm-email-row">
              <input id="jm-email" name="email" type="email"
                className={`jm-input ${errors.email ? 'err' : otpState === 'verified' ? 'ok' : ''}`}
                placeholder="your@email.com" value={form.email} onChange={handleChange}
                disabled={otpState === 'verified'} />
              {otpState !== 'verified' && (
                <button type="button" className={`jm-verify-btn ${otpState === 'sending' ? 'sending' : ''}`}
                  onClick={sendOtp}
                  disabled={otpState === 'sending' || (otpState === 'sent' && timer > 0 && otp.every(d => d === ''))}>
                  <FontAwesomeIcon icon={faShieldHalved} />
                  {otpState === 'sending' ? '' : otpState === 'sent' || otpState === 'failed' ? 'Resend' : 'Verify'}
                </button>
              )}
            </div>
            {errors.email && otpState !== 'verified' && (
              <div className="jm-err-msg"><FontAwesomeIcon icon={faXmark} /> {errors.email}</div>
            )}
            {otpState === 'verified' && (
              <div className="jm-verified-badge">
                <span className="badge-check"><FontAwesomeIcon icon={faCheck} /></span>
                Email Verified
              </div>
            )}
          </div>

          {/* ── OTP Box ── */}
          {(otpState === 'sent' || otpState === 'verifying' || otpState === 'failed') && (
            <div className="jm-otp-box" data-aos="fade-up">
              <div className="jm-otp-header">
                <span className="jm-otp-label">
                  <FontAwesomeIcon icon={faLock} className="otp-icon" />
                  Enter 4-digit OTP
                </span>
                <span className={`jm-otp-timer ${timer <= 0 ? 'expired' : ''}`}>
                  {timer > 0 ? `${Math.floor(timer / 60)}:${String(timer % 60).padStart(2, '0')}` : 'Expired'}
                </span>
              </div>

              <div className="jm-otp-digits">
                {otp.map((d, i) => (
                  <input key={i} ref={otpRefs[i]}
                    className={`jm-otp-digit ${d ? 'filled' : ''} ${otpState === 'verified' ? 'verified' : ''} ${otpState === 'failed' ? 'err-shake' : ''}`}
                    type="text" inputMode="numeric" maxLength={1}
                    value={d} onChange={e => handleOtpChange(i, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(i, e)}
                    onPaste={i === 0 ? handleOtpPaste : undefined}
                    disabled={otpState === 'verifying'}
                    autoComplete="one-time-code" />
                ))}
              </div>

              {otpState === 'verifying' && (
                <div className="jm-otp-status verifying">
                  <FontAwesomeIcon icon={faSpinner} spin /> Verifying...
                </div>
              )}
              {otpState === 'failed' && (
                <div className="jm-otp-status fail">
                  <FontAwesomeIcon icon={faCircleXmark} /> Invalid OTP — try again
                </div>
              )}

              {timer <= 0 && otpState !== 'verifying' && (
                <div className="jm-otp-actions">
                  <button type="button" className="jm-resend-btn" onClick={sendOtp}>
                    Resend OTP
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Section: Passes ── */}
          <div className="jm-divider" data-aos="fade-up" data-aos-delay="100">
            <span><FontAwesomeIcon icon={faTicket} /> &nbsp;Pass Details</span>
          </div>

          <div className="jm-grid" data-aos="fade-up" data-aos-delay="150">
            <div className="jm-field">
              <label htmlFor="jm-passes">Number of Passes</label>
              <input id="jm-passes" name="passes" type="number" min="1" max="10"
                className={`jm-input ${errors.passes ? 'err' : ''}`}
                value={form.passes} onChange={handleChange} />
              {errors.passes && <div className="jm-err-msg"><FontAwesomeIcon icon={faXmark} /> {errors.passes}</div>}
            </div>
            <div className="jm-price-box">
              <div>
                <div className="jm-price-label">Total Amount</div>
                <div className="jm-price-amount">₹{PRICE * (parseInt(form.passes, 10) || 0)}</div>
              </div>
              <div className="jm-price-detail">
                {parseInt(form.passes, 10) || 0} × ₹{PRICE} per pass
              </div>
            </div>
          </div>

          {/* ── Submit ── */}
          <div className="jm-submit-area" data-aos="fade-up" data-aos-delay="200">
            <button type="submit" className="jm-submit-btn" disabled={loading}>
              {loading ? (
                <span className="jm-spinner" />
              ) : (
                <>
                  Proceed to Payment
                  <FontAwesomeIcon icon={faArrowRight} className="arrow-icon" />
                </>
              )}
            </button>
          </div>
        </form>

        {/* ── Footer ── */}
        <div className="jm-card-footer">
          <p>
            <br />Need help? Contact <a href="mailto:rcaiitkgp.payment@gmail.com">rcaiitkgp.payment@gmail.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}
